import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import { api, useAction } from '../../api/hooks';
import { Button } from '../../ui/Button';
import { ErrorAlert, QueryView } from '../../ui/feedback';
import { Checkbox, Field, FormSection, Input, ListEditor } from '../../ui/forms';
import { Card } from '../../ui/misc';

export interface ServiceIntegration {
  configured: boolean;
  source: string;
  intakeEnabled: boolean;
  allowedOrigins: string[];
  reviewOnCreate: boolean;
  version: number;
  updatedAt: string | null;
}

/**
 * JIRA-19: another platform (e.g. IAM-v2) whose users report tickets through this service account. Its backend
 * opens Jira's ticket screen in a popup with create_intake_launch; tickets record the person as external reporter.
 */
export function IntegrationCard({ serviceId }: { serviceId: string }) {
  const q = useQuery({
    queryKey: ['ai-service-integration', serviceId],
    queryFn: () => api.get<ServiceIntegration>(`/admin/ai-services/${serviceId}/integration`),
  });
  return <QueryView query={q}>{(i) => <IntegrationForm serviceId={serviceId} current={i} />}</QueryView>;
}

function IntegrationForm({ serviceId, current }: { serviceId: string; current: ServiceIntegration }) {
  const [source, setSource] = useState(current.source);
  const [intakeEnabled, setIntakeEnabled] = useState(current.intakeEnabled);
  const [origins, setOrigins] = useState(current.allowedOrigins.length ? current.allowedOrigins : ['']);
  const [reviewOnCreate, setReviewOnCreate] = useState(current.reviewOnCreate);
  const save = useAction(
    () =>
      api.put<ServiceIntegration>(`/admin/ai-services/${serviceId}/integration`, {
        source,
        intakeEnabled,
        allowedOrigins: origins.map((o) => o.trim()).filter(Boolean),
        reviewOnCreate,
        ...(current.configured ? { expectedVersion: current.version } : {}),
      }),
    { success: 'Integration saved', invalidate: [['ai-service-integration', serviceId]] },
  );
  const err = save.error instanceof ApiError ? save.error : null;
  return (
    <Card
      title="Integration"
      subtitle="Let another platform's users report tickets through this service account. The service account is the reporter; the person is recorded with it."
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(undefined);
        }}
      >
        <FormSection title="Platform">
          <Field
            label="Source"
            hint="Short label shown on tickets, e.g. iam-v2. Lowercase letters, digits and dashes."
            error={err?.fieldError('source')}
          >
            {(p) => <Input {...p} value={source} onChange={(e) => setSource(e.target.value)} required />}
          </Field>
        </FormSection>
        <FormSection
          title="Report popup"
          description="The platform's backend calls create_intake_launch and opens the returned URL in a popup. People fill in Jira's ticket form or draft with the assistant; the popup posts the created ticket key back to the page that opened it."
        >
          <Checkbox
            label="Allow report and ticket-view popups"
            description="Needs the Create tickets capability on the project; Discuss also allows attachments."
            checked={intakeEnabled}
            onChange={setIntakeEnabled}
          />
          <Field
            label="Allowed origins"
            hint="Pages on these origins may open the popup and receive its result, e.g. https://iam-v2.lixionary.com"
            error={err?.fieldError('allowedOrigins')}
          >
            {() => (
              <ListEditor
                label="Origin"
                items={origins}
                onChange={setOrigins}
                addLabel="Add origin"
                placeholder="https://"
              />
            )}
          </Field>
        </FormSection>
        <FormSection title="Assistant">
          <Checkbox
            label="Review tickets on create"
            description="Queues an assistant review for every ticket this service account creates, so admins see its questions and suggestions on the ticket."
            checked={reviewOnCreate}
            onChange={setReviewOnCreate}
          />
        </FormSection>
        <ErrorAlert error={save.error && !err?.details.some((d) => d.field) ? save.error : null} />
        <div className="form-actions">
          <Button type="submit" loading={save.isPending}>
            Save integration
          </Button>
        </div>
      </form>
    </Card>
  );
}
